import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  IClientPassengerBrief,
  IClientTripFull,
  IClientTripPriceCalculationFull,
  IClientTripPriceCalculationPost,
} from '@bustickus/models-client';
import { ApiPayment, ApiTrips } from 'common/api';
import { Checkbox, Flex, Message } from 'components';
import { ModalLoader, PageSidebar } from 'shared/components';
import { ModalSelectPassenger, PassengerList, PaymentBlock, TripCard } from './components';
import { getQueryParam } from './helpers';
import { handleErrorAsync } from 'common/handlers';
import './style.scss';

function PageBooking() {
  const [searchParams] = useSearchParams();
  const tripId = getQueryParam(searchParams, 'trip');
  const backTripId = getQueryParam(searchParams, 'back');
  const departureCityId = getQueryParam(searchParams, 'departure');
  const arrivalCityId = getQueryParam(searchParams, 'arrival');

  const [trip, setTrip] = useState<IClientTripFull | null>(null);
  const [backTrip, setBackTrip] = useState<IClientTripFull | null>(null);
  const [passengers, setPassengers] = useState<IClientPassengerBrief[]>([]);
  const [selectPassengerModal, setSelectPassengerModal] = useState<boolean>(false);
  const [calculations, setCalculations] = useState<IClientTripPriceCalculationFull | null>(null);

  const tripDate = trip != null ? new Date(trip.points[0].departureDate ?? '') : null;

  useEffect(() => {
    loadTrip();
    loadBackTrip();
  }, []);

  const loadTrip = async () =>
    handleErrorAsync(async () => {
      if (tripId == null) {
        throw new Error();
      }

      const apiTrips = ApiTrips.getInstance();
      setTrip(await apiTrips.getById(tripId));
    });

  const loadBackTrip = async () => {
    if (backTripId != null) {
      const apiTrips = ApiTrips.getInstance();
      setBackTrip(await apiTrips.getById(backTripId));
    }
  };

  const loadCalculations = async (passengers: IClientPassengerBrief[]) => {
    if (passengers.length === 0) {
      setCalculations(null);
    } else {
      const api = ApiPayment.getInstance();
      const requests: IClientTripPriceCalculationPost[] = [];

      if (trip != null) {
        passengers.map((x) => {
          const request: IClientTripPriceCalculationPost = {
            tripId: trip.id,
            passengerId: x.id,
            departureCityId: departureCityId,
            arrivalCityId: arrivalCityId,
          };

          requests.push(request);
        });
      }

      if (backTrip != null) {
        passengers.map((x) => {
          const request: IClientTripPriceCalculationPost = {
            tripId: backTrip.id,
            passengerId: x.id,
            departureCityId: arrivalCityId,
            arrivalCityId: departureCityId,
          };

          requests.push(request);
        });
      }

      const calculations = await api.calculate(requests);
      setCalculations(calculations);
    }
  };

  const handlePassengerSelect = (passenger: IClientPassengerBrief) => {
    const newPassengers = [...passengers, passenger];
    setPassengers(newPassengers);
    setSelectPassengerModal(false);
    loadCalculations(newPassengers);
  };

  const handlePassengerRemove = (passenger: IClientPassengerBrief) => {
    const newPassengers = passengers.filter((x) => x.id !== passenger.id);
    setPassengers(newPassengers);
    loadCalculations(newPassengers);
  };

  return (
    <PageSidebar
      title='Бронирование билетов'
      frameTitle='Покупка билетов'
    >
      {trip == null && <ModalLoader />}

      {selectPassengerModal && trip != null && (
        <ModalSelectPassenger
          onClose={() => setSelectPassengerModal(false)}
          onSelect={handlePassengerSelect}
          date={tripDate != null && !Number.isNaN(tripDate.getTime()) ? tripDate : new Date()}
          needPassport={trip.documents.needPassport || backTrip?.documents.needPassport === true}
          needForeignPassport={trip.documents.needForeignPassport || backTrip?.documents.needForeignPassport === true}
          visaIds={trip.documents.visas.map((x) => x.id)}
          selectedPassengers={passengers}
        />
      )}

      <Flex
        gap={20}
        direction='column'
      >
        <Flex
          gap={10}
          direction='column'
        >
          {trip != null && (
            <TripCard
              trip={trip}
              departureCityId={departureCityId}
              arrivalCityId={arrivalCityId}
            />
          )}

          {backTrip != null && (
            <TripCard
              trip={backTrip}
              departureCityId={arrivalCityId}
              arrivalCityId={departureCityId}
            />
          )}
        </Flex>

        {trip?.isAvailable && (
          <PassengerList
            passengers={passengers}
            onAddClick={() => setSelectPassengerModal(true)}
            onRemoveClick={handlePassengerRemove}
          />
        )}

        <PaymentBlock
          trip={trip}
          back={backTrip}
          passengers={passengers}
          aCityId={arrivalCityId}
          dCityId={departureCityId}
          calculations={calculations}
        />
        <div className='booking-scroll-filler-kIsf' />
      </Flex>
    </PageSidebar>
  );
}

export default PageBooking;
